import heic2any from 'heic2any';
import { getTaskStatusBadgeClasses } from '../constants/taskColors.js';
import { formService } from './formService.js';
import { getSessionToken } from './http/auth.js';
import { HttpError, httpClient } from './http/httpClient.js';
import { storageImageService } from './storageImageService.js';
import { taskService } from './tasks/taskService.js';



const toHistoryResponse = ({ tasks, pagination, payload }) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      ...payload,
      tasks,
      pagination: payload.pagination || pagination || null
    };
  }

  return tasks;
};

// ============================================================================
// Utility helpers
// ============================================================================

export function getUserIdFromToken() {
  try {
    const token = getSessionToken();
    if (!token) return null;

    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.id || null;
  } catch {
    return null;
  }
}

export function validateOtherReason(text) {
  const words = text.trim().split(/\s+/).filter((word) => word.length > 0);
  return words.length >= 5;
}

export function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export async function compressImageToJPG(file, quality = 0.8, maxWidth = 1920, maxHeight = 1920) {
  if (!file.type.startsWith('image/') && !file.name.match(/\.(heic|heif)$/i)) {
    return file;
  }

  const isHeic = file.type === 'image/heic'
    || file.type === 'image/heif'
    || file.name.match(/\.(heic|heif)$/i);

  let workingFile = file;

  if (isHeic) {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality
      });

      const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      const fileName = file.name.replace(/\.(heic|heif)$/i, '') + '.jpg';
      workingFile = new File([blob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
    } catch (error) {
      console.error('Error convirtiendo HEIC a JPG:', error);
      return file;
    }
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const image = new Image();

      image.onload = () => {
        let width = image.width;
        let height = image.height;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir la imagen'));
              return;
            }

            const fileName = workingFile.name.replace(/\.[^/.]+$/, '') + '.jpg';
            const compressedFile = new File([blob], fileName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };

      image.onerror = () => reject(new Error('Error al cargar la imagen'));
      image.src = event.target.result;
    };

    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsDataURL(workingFile);
  });
}

// ============================================================================
// Task operations
// ============================================================================

export async function fetchTasks(day = 'today') {
  const { tasks, payload } = await taskService.getAssignedTasks({ day });
  return {
    tasks,
    routePlans: Array.isArray(payload?.routePlans) ? payload.routePlans : []
  };
}

export async function fetchTaskHistory(filters = {}) {
  const result = await taskService.getMyHistory(filters);
  return toHistoryResponse(result);
}

export async function fetchFormResponsesByTask(taskId) {
  const { responses } = await formService.getFormResponses({ taskId });
  return responses;
}

export async function updateTaskStatus(taskId, action) {
  return taskService.updateTaskStatus(taskId, action);
}

export async function cancelTask(taskId, cancelData) {
  return taskService.cancelTask(taskId, {
    reason: cancelData.reason,
    observation: cancelData.observation || null,
    requiresReschedule: cancelData.requiresReschedule,
    cancelFormData: {
      reason: cancelData.reason,
      observation: cancelData.observation || null,
      requiresReschedule: cancelData.requiresReschedule,
      cancelledAt: new Date().toISOString(),
      taskId,
      taskTitle: cancelData.taskTitle
    }
  });
}

export async function finishTask(taskId) {
  return taskService.finishTask(taskId);
}

export async function startTaskAfterGeneralForm(taskId) {
  return taskService.startTask(taskId);
}

export async function requestTaskValidation(taskId, reasonTechnician) {
  return taskService.validateTask(taskId, { reasonTechnician });
}

// ============================================================================
// Form operations
// ============================================================================

export async function fetchFormsByTaskType(taskTypeId, type = null) {
  return formService.getFormsByTaskType(taskTypeId, type);
}

export async function submitFormResponse(taskId, data) {
  return taskService.completeTaskForm(taskId, data);
}

export async function checkFormCompletion(taskId) {
  return taskService.checkFormCompletion(taskId);
}

export async function uploadImages(imagesByField, { formId, taskId, technicianEmail }, onProgress = null) {
  const fieldNames = Object.keys(imagesByField);
  const totalImages = fieldNames.reduce((sum, fieldName) => sum + imagesByField[fieldName].length, 0);
  let uploadedCount = 0;

  for (let fieldIndex = 0; fieldIndex < fieldNames.length; fieldIndex += 1) {
    const fieldName = fieldNames[fieldIndex];
    const images = imagesByField[fieldName];
    const folderName = buildFolderName(formId, taskId, technicianEmail, fieldName);
    try {
      await storageImageService.uploadStorageImages({
        folderName,
        files: images
      });
      uploadedCount += images.length;
      if (onProgress) {
        onProgress(Math.min((uploadedCount / totalImages) * 100, 100));
      }
    } catch (error) {
      const remainingFilesByField = {
        [fieldName]: images
      };

      fieldNames.slice(fieldIndex + 1).forEach((remainingFieldName) => {
        remainingFilesByField[remainingFieldName] = imagesByField[remainingFieldName];
      });

      error.uploadSummary = {
        totalImages,
        uploadedCount,
        remainingFilesByField
      };
      console.error(`Error al subir imágenes del campo ${fieldName}:`, error);
      throw error;
    }
  }

  if (onProgress) {
    onProgress(100);
  }
}

export function buildFolderName(formId, taskId, technicianEmail, fieldName) {
  return `${formId}_${taskId}_${technicianEmail}_${fieldName}`;
}

export async function deleteImage(fileId) {
  return storageImageService.deleteStorageImage({ fileId });
}

export async function getImagesByFolder({ formId, taskId, technicianEmail, fieldName, offset = 0, pageSize = null }) {
  const folderName = buildFolderName(formId, taskId, technicianEmail, fieldName);

  try {
    const result = await storageImageService.getStorageImagesByFolder({
      folderName,
      offset,
      pageSize
    });

    return {
      success: result.success,
      files: result.files,
      totalCount: result.totalCount
    };
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      throw new Error('HTTP error! status: 404');
    }

    throw error;
  }
}

// ============================================================================
// Notifications
// ============================================================================

export async function sendEnCaminoNotification(taskId) {
  try {
    await httpClient.post('/api/client/notification', {
      data: { taskId }
    });
  } catch (error) {
    console.error('Error enviando notificacion en camino:', error);
    throw error;
  }
}

// ============================================================================
// Geolocation helpers
// ============================================================================

export function isWithinRadius(techLat, techLng, clientLat, clientLng, radius = 159) {
  if (!techLat || !techLng || !clientLat || !clientLng) return false;

  const earthRadius = 6371e3;
  const phi1 = techLat * Math.PI / 180;
  const phi2 = clientLat * Math.PI / 180;
  const deltaPhi = (clientLat - techLat) * Math.PI / 180;
  const deltaLambda = (clientLng - techLng) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2)
    + Math.cos(phi1) * Math.cos(phi2)
    * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadius * c;
  return distance <= radius;
}

export function openGoogleMaps(lat, lng, address) {
  if (lat && lng) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  } else if (address) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  }
}

export function openWaze(lat, lng, address) {
  if (lat && lng) {
    window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank');
  } else if (address) {
    window.open(`https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`, '_blank');
  }
}

export function getCurrentPosition() {
  return { lat: -31.53178188337232, lng: -68.53982072174587 };
}

// ============================================================================
// Task timer helpers
// ============================================================================

export function getTaskElapsedTime(startedAt) {
  if (!startedAt) return 0;
  return Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
}

// ============================================================================
// UI validation helpers
// ============================================================================

export function validateForm(fields, fieldValues) {
  if (!fields || fields.length === 0) {
    return false;
  }

  return fields.every((field, index) => {
    if (!field.required) {
      return true;
    }

    const value = fieldValues[index];

    if (field.type === 'file' || field.type === 'image') {
      return value && value.length > 0;
    }

    if (field.type === 'checkbox') {
      return value === true || value === false;
    }

    return value && value.toString().trim() !== '';
  });
}

export function getStatusColor(status) {
  return getTaskStatusBadgeClasses(status);
}

export function getPriorityColor(priority) {
  switch (priority) {
    case 'alta':
      return 'bg-error/12 text-error border-error/30';
    case 'media':
      return 'bg-warning/12 text-warning border-warning/30';
    case 'baja':
      return 'bg-success/12 text-success border-success/30';
    default:
      return 'bg-surface-2 text-content-muted border-line';
  }
}
